import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import { useCreatePointsTier, useUpdatePointsTier } from '../api/coupons.queries';
import { pointsTierSchema, type PointsTierFormValues } from '../schemas/coupon.schema';
import type { PointsTier } from '../types/coupons.types';
import { BrandScopeField } from './brand-scope-field';

const FIELDS = [
  'pointsCost',
  'percentOff',
  'maxDiscountAmount',
  'brandIds',
  'validityDays',
] as const;

function numberValue(value: number | null) {
  return value === null || Number.isNaN(value) ? '' : value;
}

/**
 * What a customer's points buy: a personal coupon with this percent, cap and brands, valid for
 * this many days from the conversion. A change applies to the next conversion only.
 */
export function PointsTierDialog({
  countryId,
  countryName,
  currency,
  tier,
  onClose,
}: {
  countryId: number;
  countryName: string;
  currency: string | null;
  tier?: PointsTier;
  onClose: () => void;
}) {
  const create = useCreatePointsTier(countryId);
  const update = useUpdatePointsTier();
  const mutation = tier ? update : create;
  const form = useForm<PointsTierFormValues>({
    resolver: zodResolver(pointsTierSchema),
    defaultValues: {
      pointsCost: tier?.pointsCost ?? 100,
      percentOff: tier?.percentOff ?? 10,
      maxDiscountAmount: tier?.maxDiscountAmount ?? null,
      appliesToAllBrands: tier?.appliesToAllBrands ?? true,
      brandIds: tier?.brands.map((b) => b.brandId) ?? [],
      validityDays: tier?.validityDays ?? 30,
    },
  });

  const appliesToAllBrands = useWatch({ control: form.control, name: 'appliesToAllBrands' });
  const brandIds = useWatch({ control: form.control, name: 'brandIds' });

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const submit = (values: PointsTierFormValues) => {
    const input = { ...values, brandIds: values.appliesToAllBrands ? [] : values.brandIds };
    if (tier) update.mutate({ id: tier.id, values: input }, { onSuccess: onClose });
    else create.mutate(input, { onSuccess: onClose });
  };

  const numberField = (
    name: 'pointsCost' | 'percentOff' | 'validityDays',
    label: string,
    description: string,
    step: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              inputMode="decimal"
              min={1}
              step={step}
              value={numberValue(field.value)}
              onChange={(event) =>
                field.onChange(event.target.value === '' ? Number.NaN : Number(event.target.value))
              }
            />
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">
            {tier ? 'Edit points tier' : 'New points tier'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Customers in {countryName} exchange points for a personal coupon. Points spent on a
            coupon are not returned if it expires unused.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate className="space-y-4 text-xs" onSubmit={form.handleSubmit(submit)}>
            <div className="grid gap-4 sm:grid-cols-2">
              {numberField('pointsCost', 'Points', 'What the customer spends.', '1')}
              {numberField('percentOff', 'Percent off', '1–90%.', '0.01')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="maxDiscountAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum discount</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={numberValue(field.value)}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value === '' ? null : Number(event.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      {currency ?? 'Settlement currency'}. Empty = no cap.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {numberField('validityDays', 'Valid for (days)', 'From the conversion.', '1')}
            </div>
            <FormField
              control={form.control}
              name="brandIds"
              render={({ fieldState }) => (
                <FormItem>
                  <BrandScopeField
                    idPrefix="tier"
                    countryId={countryId}
                    appliesToAllBrands={appliesToAllBrands}
                    brandIds={brandIds}
                    knownBrands={tier?.brands ?? []}
                    error={fieldState.error?.message}
                    onChange={(value) => {
                      form.setValue('appliesToAllBrands', value.appliesToAllBrands);
                      form.setValue('brandIds', value.brandIds, {
                        shouldValidate: form.formState.isSubmitted,
                      });
                    }}
                  />
                </FormItem>
              )}
            />
            {formError && (
              <p role="alert" className="text-destructive">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                {tier ? 'Save' : 'Add tier'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
